import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: @types files need convex Id type

// ============================================================================
// AI Reply Settings
// ============================================================================

export type TAiReplyMode = 'draft' | 'auto' | 'voice_call';
export type TAiReplyPlatform = 'telegram' | 'whatsapp' | 'gmail';

export interface IAiReplySettings {
  _id: Id<'aiReplySettings'>;
  userId: Id<'users'>;
  enabled: boolean;
  mode: TAiReplyMode;
  enabledPlatforms: TAiReplyPlatform[];
  followUpEnabled: boolean;
  followUpDelays: number[]; // Minutes
  maxFollowUps: number;
  gmailLabelsEnabled: boolean;
  // Granular per-platform, per-chat-type AI control
  telegramEnabled?: boolean;
  telegramGroupsEnabled?: boolean;
  telegramPersonalEnabled?: boolean;
  whatsappEnabled?: boolean;
  whatsappGroupsEnabled?: boolean;
  whatsappPersonalEnabled?: boolean;
  emailEnabled?: boolean;
  // Communication style analysis settings
  analysisLookbackDays?: number;
  analyzeGmail?: boolean;
  analyzeWhatsapp?: boolean;
  analyzeTelegram?: boolean;
  useStyleGmail?: boolean;
  useStyleWhatsapp?: boolean;
  useStyleTelegram?: boolean;
  analyzeWhatsappGroups?: boolean;
  analyzeTelegramGroups?: boolean;
  createdAt: number;
  updatedAt: number;
}

// ============================================================================
// Contact Overrides
// ============================================================================

export interface IAiReplyContactOverride {
  _id: Id<'aiReplyContactOverrides'>;
  userId: Id<'users'>;
  contactIdentifier: string;
  platform: TAiReplyPlatform;
  contactName?: string;
  disableAutoReply: boolean;
  disableFollowUp: boolean;
  disableStyleUsage?: boolean;
  createdAt: number;
}

// ============================================================================
// AI Drafts
// ============================================================================

export type TAiDraftStatus = 'pending' | 'sent' | 'edited_and_sent' | 'cancelled' | 'superseded';

export interface IAiReplyDraft {
  _id: Id<'aiReplyDrafts'>;
  userId: Id<'users'>;
  platform: TAiReplyPlatform;
  contactIdentifier: string;
  contactName: string;
  incomingMessageRef: string;
  draftText: string;
  classification: TMessageClassification;
  status: TAiDraftStatus;
  editedText?: string;
  createdAt: number;
  sentAt?: number;
  relatedLeadId?: Id<'leads'>;
}

// ============================================================================
// Message Classification
// ============================================================================

export type TMessageClassification =
  | 'insurance_inquiry'
  | 'property_inquiry'
  | 'partner_request'
  | 'company_admin'
  | 'policy_renewal'
  | 'follow_up_answer'
  | 'personal'
  | 'payment'
  | 'notification'
  | 'promotion'
  | 'spam'
  | 'uncertain';

export const MESSAGE_CLASSIFICATIONS: Record<
  TMessageClassification,
  {
    label: string;
    requiresReply: boolean;
    gmailLabel: string | null;
  }
> = {
  insurance_inquiry: {
    label: 'Insurance Inquiry',
    requiresReply: true,
    gmailLabel: 'AI/Insurance',
  },
  property_inquiry: { label: 'Property Inquiry', requiresReply: true, gmailLabel: 'AI/Property' },
  partner_request: { label: 'Partner Request', requiresReply: true, gmailLabel: 'AI/Partner' },
  company_admin: { label: 'Company Admin', requiresReply: true, gmailLabel: 'AI/Company' },
  policy_renewal: { label: 'Policy Renewal', requiresReply: true, gmailLabel: 'AI/Renewal' },
  follow_up_answer: { label: 'Follow-up Answer', requiresReply: true, gmailLabel: null },
  personal: { label: 'Personal', requiresReply: false, gmailLabel: null },
  payment: { label: 'Payment', requiresReply: false, gmailLabel: 'AI/Payment' },
  notification: { label: 'Notification', requiresReply: false, gmailLabel: 'AI/Notification' },
  promotion: { label: 'Promotion', requiresReply: false, gmailLabel: 'AI/Promotion' },
  spam: { label: 'Spam', requiresReply: false, gmailLabel: 'AI/Spam' },
  uncertain: { label: 'Needs Review', requiresReply: false, gmailLabel: 'AI/Review' },
};

export const GMAIL_AI_LABELS = [
  'AI/Insurance',
  'AI/Property',
  'AI/Partner',
  'AI/Company',
  'AI/Renewal',
  'AI/Payment',
  'AI/Notification',
  'AI/Promotion',
  'AI/Spam',
  'AI/Review',
] as const;

export type TGmailAiLabel = (typeof GMAIL_AI_LABELS)[number];

// ============================================================================
// Contact Notes
// ============================================================================

export type TContactNoteType = 'preference' | 'context' | 'relationship' | 'request_history';

export interface IContactNote {
  _id: Id<'contactNotes'>;
  userId: Id<'users'>;
  contactIdentifier: string;
  platform: TAiReplyPlatform;
  noteType: TContactNoteType;
  content: string;
  createdAt: number;
  updatedAt: number;
}

// ============================================================================
// Style Profile
// ============================================================================

export interface IAiStyleProfile {
  _id: Id<'aiStyleProfile'>;
  userId: Id<'users'>;
  profile: string;
  examples: string[];
  analyzedAt: number;
  messageCount: number;
  platform?: TAiReplyPlatform;
  isEdited?: boolean;
  editedProfile?: string;
}

// ============================================================================
// Learned Rules
// ============================================================================

// NEW_FUNCTION_JUSTIFIED: New types for the aiLearnedRules table — no existing types cover rule source/type or the learned rule entity.
export type TRuleType = 'always_do' | 'never_do' | 'prefer' | 'avoid';
export type TRuleSource = 'auto' | 'manual';

export interface ILearnedRule {
  _id: Id<'aiLearnedRules'>;
  userId: Id<'users'>;
  platform?: TAiReplyPlatform;
  rule: string;
  ruleType: TRuleType;
  source: TRuleSource;
  observedCount: number;
  isActive: boolean;
  exampleBefore?: string;
  exampleAfter?: string;
  createdAt: number;
  updatedAt: number;
}

export const RULE_TYPE_LABELS: Record<TRuleType, string> = {
  always_do: 'Always',
  never_do: 'Never',
  prefer: 'Prefer',
  avoid: 'Avoid',
};

// NEW_FUNCTION_JUSTIFIED: Props interface for the LearnedRulesList component — no existing interface covers rule list management with add/update/toggle/delete callbacks.
export interface ILearnedRulesListProps {
  rules: ILearnedRule[];
  platform?: TAiReplyPlatform; // undefined = showing global rules
  onAddRule: (rule: string, ruleType: TRuleType, platform?: TAiReplyPlatform) => Promise<void>;
  onUpdateRule: (
    ruleId: Id<'aiLearnedRules'>,
    updates: { rule?: string; ruleType?: TRuleType }
  ) => Promise<void>;
  onToggleRule: (ruleId: Id<'aiLearnedRules'>, isActive: boolean) => Promise<void>;
  onDeleteRule: (ruleId: Id<'aiLearnedRules'>) => Promise<void>;
}

// ============================================================================
// Follow-up Tasks
// ============================================================================

export type TFollowUpStatus = 'pending' | 'sent' | 'cancelled';

export interface IFollowUpTask {
  _id: Id<'followUpTasks'>;
  userId: Id<'users'>;
  contactIdentifier: string;
  platform: TAiReplyPlatform;
  contactName: string;
  originalMessageRef: string;
  originalMessageText?: string;
  threadId?: string; // Gmail thread ID for email threading
  step: number; // 1, 2, or 3
  scheduledAt: number;
  status: TFollowUpStatus;
  sentAt?: number;
  createdAt: number;
}

// ============================================================================
// Default Settings
// ============================================================================

export const DEFAULT_AI_REPLY_SETTINGS: {
  readonly enabled: boolean;
  readonly mode: TAiReplyMode;
  readonly enabledPlatforms: readonly TAiReplyPlatform[];
  readonly followUpEnabled: boolean;
  readonly followUpDelays: readonly number[];
  readonly maxFollowUps: number;
  readonly gmailLabelsEnabled: boolean;
  readonly telegramEnabled: boolean;
  readonly telegramGroupsEnabled: boolean;
  readonly telegramPersonalEnabled: boolean;
  readonly whatsappEnabled: boolean;
  readonly whatsappGroupsEnabled: boolean;
  readonly whatsappPersonalEnabled: boolean;
  readonly emailEnabled: boolean;
} = {
  enabled: false,
  mode: 'draft',
  enabledPlatforms: ['telegram', 'whatsapp', 'gmail'],
  followUpEnabled: true,
  followUpDelays: [60, 180, 540], // 1hr, 3hr, 9hr (next day 9AM handled by cron logic)
  maxFollowUps: 3,
  gmailLabelsEnabled: true,
  telegramEnabled: true,
  telegramGroupsEnabled: false,
  telegramPersonalEnabled: true,
  whatsappEnabled: true,
  whatsappGroupsEnabled: false,
  whatsappPersonalEnabled: true,
  emailEnabled: true,
};

export const DEFAULT_STYLE_ANALYSIS_SETTINGS = {
  analysisLookbackDays: 30,
  analyzeGmail: true,
  analyzeWhatsapp: true,
  analyzeTelegram: true,
  useStyleGmail: true,
  useStyleWhatsapp: true,
  useStyleTelegram: true,
  analyzeWhatsappGroups: false,
  analyzeTelegramGroups: false,
} as const;

export const DEFAULT_TIMEZONE = 'Europe/Nicosia';

// ============================================================================
// Dashboard
// ============================================================================

export type TDashboardTimeRange = '24h' | '7d' | '30d';

export interface IDashboardStats {
  unansweredCount: number;
  answeredCount: number;
  answeredByAi: number;
  answeredByHuman: number;
}

export interface IRenewalPolicy {
  policyId: Id<'insurancePolicies'>;
  policyNumber: string;
  customerName: string;
  insuranceType: string;
  expiryDate: number;
  daysUntilExpiry: number;
}

export type TRenewalTimeRange = '30d' | '60d' | '90d';

// ============================================================================
// AI Contact Settings (ContactPanel sub-component)
// ============================================================================

export interface IAiContactSettingsProps {
  contactIdentifier: string;
  platform: TAiReplyPlatform;
  contactName?: string;
}

// ============================================================================
// AI Draft Bubble
// ============================================================================

export interface IAiDraftBubbleProps {
  draft: IAiReplyDraft;
  onSend: (draftId: IAiReplyDraft['_id'], text: string) => Promise<void>;
  onCancel: (draftId: IAiReplyDraft['_id']) => Promise<void>;
  onEditAndSend?: (draftText: string) => void;
}

// ============================================================================
// AI Reply Button
// ============================================================================

export interface IAiReplyButtonProps {
  contactIdentifier: string;
  platform: TAiReplyPlatform;
  contactName?: string;
}

// ============================================================================
// AI Notes Section (ContactPanel sub-component)
// ============================================================================

export interface IAiNotesSectionProps {
  contactIdentifier: string;
  platform: TAiReplyPlatform;
}
